import { CookieNames } from '@/constants/Cookies';
import { setCookie } from '@/lib/cookie-helpers';

// Base URL is env-driven (nuxt.config `hexagon` <- HEXAGON_URL), rotating per environment without a
// code change. When HEXAGON_URL is unset, URL builders return '' and no redirect fires, keeping
// flows testable locally without a Hexagon destination.

// --- $1 Premium People trial handoff (BIO-1350) ---
//
// NOTE: a clean landing on employee-count also needs the BIO-1380 activation-gate wiring in Hexagon;
// until that ships, Hexagon may route a fresh trial company through its normal activation entry.
export const HEXAGON_TRIAL_HANDOFF_PATH = '/activation/employee-count';

const hexagonBaseUrl = (process.env.hexagon || '').replace(/\/$/, '');

export const HEXAGON_TRIAL_HANDOFF_URL = hexagonBaseUrl
  ? `${hexagonBaseUrl}${HEXAGON_TRIAL_HANDOFF_PATH}`
  : '';

// Written to the plan cookie: marks the account as a Premium People plan customer.
export const PP_PLAN_CUSTOMER_VALUE = 'PREMIUM_PEOPLE';

interface HexagonHandoffInput {
  /** The (already-allowlisted) trial length in days. Written to the trial cookie so Hexagon knows
   *  both that a trial is active and how long it runs. */
  trialDurationDays: number;
}

/**
 * Hand a freshly-started $1 Premium People trial off to Hexagon (BIO-1350).
 *
 * Auth is already carried by the existing access-token cookie (`CookieNames.ACCESS_TOKEN`), which
 * cookie-helpers sets on the shared parent domain (`process.env.host`, e.g. `.bambee.com`); Hexagon
 * on a `*.bambee.com` subdomain reads it as-is, so no new auth cookie is minted here.
 *
 * On top of that, this writes two flag cookies on the same shared domain so Hexagon can key on each
 * independently:
 *   1. PP_PLAN_CUSTOMER — the account is on a Premium People plan.
 *   2. PP_TRIAL — a trial is active, valued with the trial length in days.
 *
 * The redirect is deliberately decoupled: with HEXAGON_TRIAL_HANDOFF_URL empty, only the cookies are
 * written. Wire the destination when it exists.
 */
export function startTrialHexagonHandoff({
  trialDurationDays,
}: HexagonHandoffInput): void {
  setCookie(CookieNames.PP_PLAN_CUSTOMER, PP_PLAN_CUSTOMER_VALUE);
  setCookie(CookieNames.PP_TRIAL, String(trialDurationDays));

  if (HEXAGON_TRIAL_HANDOFF_URL) {
    window.location.assign(HEXAGON_TRIAL_HANDOFF_URL);
  }
}

// --- Lead checkout handoff (BIO-2258) ---
//
// A lead company that arrives at the legacy /payment page (old email link, bookmark) is sent to
// Hexagon's /pay page instead. Auth is already present via the shared .bambee.com access-token
// cookie — no extra cookie is needed here.
export const HEXAGON_LEAD_CHECKOUT_PATH = '/pay';

/**
 * Build the Hexagon lead checkout URL. Returns '' when HEXAGON_URL is unset so callers can
 * fall back gracefully (e.g. stay on /payment in a dev environment without the var).
 *
 * @param ach  When true, appends `?ach=1` to send the user into bank-only checkout.
 */
export const buildHexagonLeadCheckoutUrl = (ach?: boolean): string => {
  if (!hexagonBaseUrl) {
    return '';
  }
  const params = ach ? '?ach=1' : '';

  return `${hexagonBaseUrl}${HEXAGON_LEAD_CHECKOUT_PATH}${params}`;
};
